import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';

/**
 * Table: tabular data (transaction history, upcoming payments, saved methods) on desktop.
 *
 * This is a desktop pattern: below `lg`, reflow rows into stacked card list items rather than
 * letting the table scroll sideways. Wrap it in a `Card` for the standard bordered surface.
 *
 * For a scrolling history, pass `maxHeight` here and `sticky` on `TableHeader`.
 */
function Table({
  className,
  maxHeight,
  containerClassName,
  ...props
}: React.ComponentProps<'table'> & {
  /** Caps the scroll container's height. A bare number is treated as pixels. */
  maxHeight?: string | number;
  containerClassName?: string;
}) {
  const cappedHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  return (
    <div
      data-slot="table-container"
      className={cn(
        'relative w-full overflow-x-auto',
        cappedHeight && 'max-h-[var(--table-max-height)] overflow-y-auto',
        containerClassName,
      )}
      // A caller-supplied height can't be a static utility, so it rides in on a CSS variable that
      // the `max-h-[…]` class above reads.
      style={cappedHeight ? ({ '--table-max-height': cappedHeight } as React.CSSProperties) : undefined}
    >
      <table data-slot="table" className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  );
}

function TableHeader({
  className,
  sticky,
  ...props
}: React.ComponentProps<'thead'> & {
  /** Pins the header while the body scrolls. Pair with `maxHeight` on `Table`. */
  sticky?: boolean;
}) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b', sticky && 'sticky top-0 z-10 bg-card', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted', className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'whitespace-nowrap p-3 text-left align-middle text-xs font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('p-3 align-middle text-foreground [&:has([role=checkbox])]:pr-0', className)}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption data-slot="table-caption" className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  );
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
